import { useState } from 'react'

import { ApiError, api } from '../api'
import type {
  BaziPeriodDto,
  CycleBase,
  CyclePeriod as CyclePeriodId,
  CyclesResponse,
  DecadePalaceDto,
  LuckDto,
  PeriodCardDto,
  PeriodCardResponse,
} from '../../contracts/cycles'
import type { SystemsResponse } from '../../contracts/api'
import { Bases } from '../components/Bases'
import { Collapsible } from '../components/Collapsible'
import { JournalMark } from '../components/JournalMark'
import { ElementFlow } from '../components/viz/ElementFlow'
import { PalaceRing } from '../components/viz/PalaceRing'
import type { PalaceRingItem } from '../components/viz/PalaceRing'
import { WaveStrip } from '../components/viz/WaveStrip'
import { elementIdFrom, palaceShort } from '../colors'
import { daysLeftLabel, formatRange } from '../dates'
import { useNav } from '../nav'
import { haptic } from '../telegram'
import { useCharts } from './chartsData'
import { cachedPeriodCard, rememberPeriodCard, useCycles } from './cyclesData'
import './cycles.css'

const PERIOD_NOTE: Record<CyclePeriodId, string> = {
  wave: 'волна',
  month: 'месяц',
  year: 'год',
  decade: 'десятилетие',
}

const HIDDEN_ROLE: Record<string, string> = {
  main: 'главный',
  middle: 'средний',
  residual: 'остаточный',
}

type CardState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; card: PeriodCardDto }
  | { kind: 'locked'; message: string }
  | { kind: 'error'; message: string }

/**
 * Разбор одного периода: что это за отрезок, из чего он следует и что с ним
 * делать. Структурный слой (визуал, «что это значит», основания) виден всегда;
 * личная карточка от модели собирается по кнопке — её нельзя греть заранее,
 * каждая сборка стоит денег.
 */
export function CyclePeriod({ period }: { period: CyclePeriodId }) {
  const { data, error } = useCycles()
  const charts = useCharts()

  if (error) return <div className="state">{error}</div>
  if (!data) return <div className="state">Собираем период…</div>

  const view = periodView(data, period)

  return (
    <div className="screen cycles cycle-period">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">{PERIOD_NOTE[period]}</div>
      </div>

      <h1 className="display">{view.title}</h1>
      {view.dates && <div className="sub">{view.dates}</div>}

      <PeriodVisual data={data} period={period} charts={charts.data} />

      <div className="card period-card">
        <div className="lbl">Что это значит</div>
        {view.lines.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>

      {period === 'decade' && <DecadeSections data={data} />}

      <div className="rowlist period-sections">
        <Collapsible title="Из чего следует" note="признаки систем">
          {view.personal.length > 0 && (
            <ul className="period-personal">
              {view.personal.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          {view.hidden.length > 0 && (
            <div className="period-hidden">
              <div className="lbl">Скрытые стволы</div>
              {view.hidden.map((hidden) => (
                <div className="period-hidden-row" key={`${hidden.stemRu}-${hidden.role}`}>
                  <span>
                    {hidden.stemRu} · {hidden.elementRu}
                  </span>
                  <span className="dim">
                    {hidden.tenGodRu} · {HIDDEN_ROLE[hidden.role] ?? hidden.role}
                  </span>
                </div>
              ))}
            </div>
          )}
          <Bases items={view.bases} />
        </Collapsible>

        <Collapsible title="Личная карточка периода" note="слова про тебя, не про период вообще">
          <PersonalCard data={data} period={period} />
        </Collapsible>
      </div>
    </div>
  )
}

/* ─── шапка и тексты периода ───────────────────────────────────────────── */

interface PeriodView {
  title: string
  dates: string
  lines: string[]
  bases: CycleBase[]
  personal: string[]
  hidden: BaziPeriodDto['hidden']
}

function periodView(data: CyclesResponse, period: CyclePeriodId): PeriodView {
  if (period === 'wave') {
    return {
      title: data.wave.text.title,
      dates: `${formatRange(data.wave.startDate, data.wave.endDate)}, ${daysLeftLabel(data.wave.daysLeft)}`,
      lines: data.wave.text.lines,
      bases: data.wave.bases,
      personal: [],
      hidden: [],
    }
  }
  if (period === 'decade') {
    const span = data.decade.bazi ?? data.decade.ziwei
    return {
      title: data.decade.text.title,
      dates: span ? `${span.startYear}–${span.endYear}` : '',
      lines: data.decade.text.lines,
      bases: data.decade.bases,
      personal: data.decade.bazi?.personal ?? [],
      hidden: [],
    }
  }
  const source: BaziPeriodDto = period === 'month' ? data.month : data.year
  return {
    title: source.text.title,
    dates: `${formatRange(source.startDate, source.endDate)}, ${daysLeftLabel(source.daysLeft)}`,
    lines: source.text.lines,
    bases: source.bases,
    personal: source.personal,
    hidden: source.hidden,
  }
}

/* ─── визуал по периоду ────────────────────────────────────────────────── */

function PeriodVisual({
  data,
  period,
  charts,
}: {
  data: CyclesResponse
  period: CyclePeriodId
  charts: SystemsResponse | null
}) {
  if (period === 'wave') {
    return (
      <div className="viz-box">
        <WaveStrip days={data.wave.days} />
        <div className="viz-note">
          Волна печати «{data.wave.seal.nameRu}». Отмеченные точки — дни, связанные с твоим кином.
        </div>
      </div>
    )
  }

  if (period === 'month' || period === 'year') {
    const pillar = period === 'month' ? data.month.pillar : data.year.pillar
    const flow = (
      <div className="viz-box">
        <ElementFlow
          elements={charts?.bazi.elements ?? []}
          highlight={pillar.element}
          missing={idsOf(charts?.bazi.missing)}
          overloaded={idsOf(charts?.bazi.overloaded)}
        />
        <div className="viz-note">Стихия периода — {pillar.elementRu.toLowerCase()}, она подсвечена в кольце.</div>
      </div>
    )
    if (period === 'year' && data.year.ziwei) {
      return (
        <>
          {flow}
          <div className="viz-box">
            <PalaceRing
              palaces={ringPalaces(charts, data.year.ziwei.annualLifePalace.natalPalaceId)}
              highlight={{
                life: lifePalaceId(charts),
                body: bodyPalaceId(charts),
                decade: data.decade.ziwei?.palaceId ?? null,
                annual: data.year.ziwei.annualLifePalace.natalPalaceId,
              }}
            />
          </div>
        </>
      )
    }
    return flow
  }

  if (data.decade.ziwei) {
    return (
      <div className="viz-box">
        <PalaceRing
          palaces={ringPalaces(charts, data.decade.ziwei.palaceId)}
          highlight={{
            life: lifePalaceId(charts),
            body: bodyPalaceId(charts),
            decade: data.decade.ziwei.palaceId,
            annual: data.year.ziwei?.annualLifePalace.natalPalaceId ?? null,
          }}
        />
      </div>
    )
  }
  return null
}

function idsOf(values: string[] | undefined): string[] {
  const ids: string[] = []
  for (const value of values ?? []) {
    const id = elementIdFrom(value)
    if (id) ids.push(id)
  }
  return ids
}

function lifePalaceId(charts: SystemsResponse | null): string | null {
  return charts?.ziwei?.palaces.find((palace) => palace.isLife)?.id ?? null
}

function bodyPalaceId(charts: SystemsResponse | null): string | null {
  return charts?.ziwei?.palaces.find((palace) => palace.isBody)?.id ?? null
}

/**
 * Кольцо дворцов: имена берём из натальной карты. Если карта не построена
 * (нет времени рождения), рисуем пустое кольцо с одним подписанным сектором —
 * тем, о котором идёт речь.
 */
function ringPalaces(charts: SystemsResponse | null, highlighted: string): PalaceRingItem[] {
  const palaces = charts?.ziwei?.palaces
  if (palaces && palaces.length > 0) return palaces.map((palace) => ({ id: palace.id, titleRu: palace.titleRu }))
  const blanks: PalaceRingItem[] = Array.from({ length: 11 }, (_, index) => ({ id: `blank-${index}` }))
  return [{ id: highlighted, titleRu: palaceShort(highlighted, '') }, ...blanks]
}

/* ─── десятилетие: такт, дворец, следующее ─────────────────────────────── */

function DecadeSections({ data }: { data: CyclesResponse }) {
  const { bazi, ziwei, nextBazi, nextZiwei } = data.decade
  return (
    <>
      {bazi && (
        <div className="card period-card">
          <div className="lbl">Такт Ба Цзы</div>
          <p className="period-strong">
            {bazi.pillar.stemRu} {bazi.pillar.branchRu} · {bazi.tenGod.nameRu}
          </p>
          <p>{bazi.tenGodMeaning.essence}</p>
          <p>{bazi.tenGodMeaning.period}</p>
          <p className="dim">Перегруз: {bazi.tenGodMeaning.overload}</p>
          {bazi.personal.length > 0 && (
            <ul className="period-personal">
              {bazi.personal.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {ziwei && (
        <div className="card period-card">
          <div className="lbl">Дворец Цзы Вэй</div>
          <p className="period-strong">{ziwei.palaceRu}</p>
          <p>{ziwei.meaning.sphere}</p>
          <p>{ziwei.meaning.decade}</p>
          {ziwei.stars.length > 0 && (
            <div className="chips period-stars">
              {ziwei.stars.map((star) => (
                <span className="chip" key={star.id}>
                  {star.nameRu}
                  <span className="dim"> · {star.role}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {(nextBazi || nextZiwei) && (
        <div className="card period-card">
          <div className="lbl">Следующее десятилетие</div>
          {nextBazi && <NextLine luck={nextBazi} />}
          {nextZiwei && <NextPalaceLine palace={nextZiwei} />}
        </div>
      )}
    </>
  )
}

function NextLine({ luck }: { luck: LuckDto }) {
  return (
    <p>
      <span className="period-strong">
        {luck.pillar.stemRu} {luck.pillar.branchRu} · {luck.tenGod.nameRu}
      </span>
      <span className="dim">
        {' '}
        — {luck.startYear}–{luck.endYear}, с {luck.startAge} лет. {luck.tenGodMeaning.essence}
      </span>
    </p>
  )
}

function NextPalaceLine({ palace }: { palace: DecadePalaceDto }) {
  return (
    <p>
      <span className="period-strong">{palace.palaceRu}</span>
      <span className="dim">
        {' '}
        — {palace.startYear}–{palace.endYear}, {palace.startAge}–{palace.endAge} лет. {palace.meaning.sphere}
      </span>
    </p>
  )
}

/* ─── личная карточка периода ──────────────────────────────────────────── */

function PersonalCard({ data, period }: { data: CyclesResponse; period: CyclePeriodId }) {
  const nav = useNav()
  const access = data.cards[period]
  const cacheKey = `${period}:${access.key}`
  const [state, setState] = useState<CardState>(() => {
    const known = cachedPeriodCard(cacheKey)
    return known ? { kind: 'ready', card: known } : { kind: 'idle' }
  })

  if (!access.available && access.reason === 'no-llm') {
    return <p className="quiet">Карточку сейчас некому написать — модель не подключена. Структурный разбор выше полный.</p>
  }

  if (!access.available) {
    return (
      <div className="teaser">
        <p>
          Личная карточка периода — это не «что такое год Огня», а что этот отрезок значит для твоей карты: тема,
          на что опереться, где перегруз и одно маленькое действие.
        </p>
        <button
          type="button"
          className="btn btn-gold"
          onClick={() => {
            haptic('medium')
            nav.navigate({ screen: { kind: 'access' } })
          }}
        >
          Открыть доступ
        </button>
      </div>
    )
  }

  const build = () => {
    haptic('medium')
    setState({ kind: 'loading' })
    api
      .get<PeriodCardResponse>(`/api/cycles/card/${period}`)
      .then((result) => {
        if (result.locked) {
          setState({ kind: 'locked', message: result.message })
          return
        }
        rememberPeriodCard(cacheKey, result.card)
        setState({ kind: 'ready', card: result.card })
      })
      .catch((cause: unknown) => {
        setState({
          kind: 'error',
          message: cause instanceof ApiError ? cause.message : 'Карточка не собралась. Попробуй ещё раз',
        })
      })
  }

  if (state.kind === 'ready') {
    return (
      <>
        <PeriodCardBody card={state.card} />
        <JournalMark
          kind="period"
          refKey={cacheKey}
          ref={{ period, key: access.key, theme: state.card.theme }}
          keys={[]}
        />
      </>
    )
  }

  if (state.kind === 'locked') {
    return (
      <div className="teaser">
        <p>{state.message}</p>
        <button
          type="button"
          className="btn btn-gold"
          onClick={() => {
            haptic('medium')
            nav.navigate({ screen: { kind: 'access' } })
          }}
        >
          Открыть доступ
        </button>
      </div>
    )
  }

  return (
    <div className="teaser">
      {state.kind === 'error' && <p className="quiet">{state.message}</p>}
      <button type="button" className="btn btn-gold" disabled={state.kind === 'loading'} onClick={build}>
        {state.kind === 'loading' ? 'Собираем…' : 'Собрать карточку'}
      </button>
    </div>
  )
}

function PeriodCardBody({ card }: { card: PeriodCardDto }) {
  return (
    <div className="card day-card period-personal-card">
      <div className="lbl" style={{ color: 'var(--gold)' }}>
        Тема периода
      </div>
      <p className="day-theme">{card.theme}</p>

      <div className="day-block">
        <div className="lbl">Почему</div>
        <p>{card.why}</p>
      </div>
      <div className="day-block">
        <div className="lbl">Лучшее применение</div>
        <p>{card.useFor}</p>
      </div>
      <div className="day-block">
        <div className="lbl">Риск перегруза</div>
        <p>{card.risk}</p>
      </div>
      <div className="day-block">
        <div className="lbl">Маленькое действие</div>
        <p>{card.action}</p>
      </div>
      <div className="day-block">
        <div className="lbl">К переходу</div>
        <p>{card.transition}</p>
      </div>
    </div>
  )
}
